import React, { useState } from "react";
import { Briefcase, Cpu, ShieldAlert, Hourglass, Check, ArrowRight, RotateCcw, Sparkles } from "lucide-react";

const QUESTIONS = [
  {
    id: "layoff_risk",
    icon: Briefcase,
    keyword: "Layoffs & job loss",
    question: "Is your job at risk of layoff?",
    options: [
      { value: "yes", label: "Yes — already happening" },
      { value: "maybe", label: "Maybe — feels unstable" },
      { value: "no", label: "No, but I want a backup" },
    ],
  },
  {
    id: "weekend_work",
    icon: Hourglass,
    keyword: "Weekend work & overtime",
    question: "Do you work most weekends or do unpaid overtime?",
    options: [
      { value: "always", label: "Almost every weekend" },
      { value: "sometimes", label: "More than I'd like" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "bad_boss",
    icon: ShieldAlert,
    keyword: "Bad bosses & toxic workplace",
    question: "Are you stuck under a bad boss or toxic culture?",
    options: [
      { value: "yes", label: "Yes — burnt out" },
      { value: "mixed", label: "Some days, yes" },
      { value: "no", label: "No, just want options" },
    ],
  },
];

// Cards to display before the quiz starts
const CARDS = [
  { icon: Briefcase, keyword: "Layoffs & job loss", title: "Layoff waves never stop.", body: "Tech and startup layoffs gut teams every quarter. After years of loyal work, a single 'restructuring' email can end your salary overnight.", promise: "Build an income stream no boss can lay off." },
  { icon: Cpu, keyword: "AI replacing your role", title: "AI is rewriting your job description.", body: "Automation is reshaping roles faster than promotions arrive. Waiting for HR to 'reskill' you is a losing game.", promise: "Put an AI agent on your side, earning for you." },
  { icon: ShieldAlert, keyword: "Bad bosses, toxic workplaces", title: "Bad bosses, endless grind, no respect.", body: "Toxic managers, office politics, unpaid overtime, working weekends — yet your entire livelihood rides on one company's mood.", promise: "Build income you control. No permission needed." },
  { icon: Hourglass, keyword: "Hiring freezes & long benches", title: "Hiring freezes & long benches between jobs.", body: "Frozen budgets, stretched notice periods, and months on the bench between roles eat your savings and your confidence.", promise: "Keep earning in ₹ between roles — or instead of one." },
];

function buildAvniOpener(answers) {
  const tags = [];
  if (answers.layoff_risk === "yes") tags.push("a recent or imminent layoff");
  else if (answers.layoff_risk === "maybe") tags.push("an unstable job market");
  if (answers.weekend_work === "always") tags.push("weekend work + unpaid overtime");
  else if (answers.weekend_work === "sometimes") tags.push("creeping overtime");
  if (answers.bad_boss === "yes") tags.push("a toxic boss / culture");
  else if (answers.bad_boss === "mixed") tags.push("mixed feelings about your workplace");

  if (tags.length === 0) {
    return "I want a backup income stream that earns in ₹, without quitting my job yet. Give me 3 freelance services I could start TODAY based on common professional skills, with realistic ₹ pricing.";
  }
  const tagList = tags.length === 1 ? tags[0] : tags.slice(0, -1).join(", ") + " and " + tags.slice(-1);
  return `I'm dealing with ${tagList}. I want to stop being a corporate slave and earn in ₹ on my own terms. Without knowing my exact field yet, give me 3 high-demand freelance services I could productize from common professional skills — with realistic ₹ pricing and a 7-day plan to land the first gig. Be specific.`;
}

export default function RealitySection({ onAskAvni }) {
  const [phase, setPhase] = useState("intro"); // intro | quiz | done
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});

  const start = () => { setPhase("quiz"); setStepIdx(0); setAnswers({}); };
  const reset = () => { setPhase("intro"); setStepIdx(0); setAnswers({}); };

  const onAnswer = (value) => {
    const q = QUESTIONS[stepIdx];
    const next = { ...answers, [q.id]: value };
    setAnswers(next);
    if (stepIdx + 1 < QUESTIONS.length) {
      setStepIdx(stepIdx + 1);
    } else {
      setPhase("done");
    }
  };

  const askAvni = () => {
    const opener = buildAvniOpener(answers);
    onAskAvni?.(opener);
  };

  return (
    <section id="reality" data-testid="reality-section" className="py-24 bg-slate-50/60 border-t border-slate-200">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <header className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">The reality</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 leading-tight">
            Layoffs are the new normal. <span className="text-blue-600">Stop being a corporate slave.</span>
          </h2>
          <p className="mt-4 sm:mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            Tech layoffs, AI automation, hiring freezes, EMI stress and bad bosses have made one salary the riskiest plan there is.
            Add unpaid overtime, weekend work and the corporate grind — and the 9-to-5 stops feeling worth it. Your skills deserve a
            second engine that pays you in ₹ even when the market — or your manager — doesn&apos;t.
          </p>
        </header>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: Reality cards */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {CARDS.map((c, i) => (
              <article
                key={c.keyword}
                data-testid={`reality-card-${i}`}
                className="rounded-2xl border border-slate-200 bg-white p-6 lift"
              >
                <div className="flex items-start gap-3">
                  <span className="grid place-items-center w-10 h-10 rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100 shrink-0">
                    <c.icon size={18} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-blue-600/80">{c.keyword}</p>
                    <h3 className="mt-1 font-display text-lg font-bold text-slate-900 leading-snug">{c.title}</h3>
                  </div>
                </div>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed">{c.body}</p>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-start gap-2 text-xs font-semibold text-blue-700">
                  <Check size={14} className="mt-0.5 shrink-0" />
                  <span>{c.promise}</span>
                </div>
              </article>
            ))}
          </div>

          {/* Right: Quiz card */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div data-testid="reality-quiz" className="tracing-beam rounded-2xl bg-white border border-blue-200 p-7 shadow-[0_30px_80px_-40px_rgba(37,99,235,0.45)]">
                {phase === "intro" && <QuizIntro onStart={start} />}
                {phase === "quiz" && (
                  <QuizStep
                    q={QUESTIONS[stepIdx]}
                    idx={stepIdx}
                    total={QUESTIONS.length}
                    onAnswer={onAnswer}
                  />
                )}
                {phase === "done" && <QuizDone answers={answers} onAskAvni={askAvni} onRestart={reset} />}
              </div>
              <p className="mt-3 text-xs text-slate-500 text-center">
                No email needed. Your answers stay on this device — we just personalize Avni&apos;s response.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function QuizIntro({ onStart }) {
  return (
    <div>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium px-3 py-1">
        <Sparkles size={12} /> 30-second reality check
      </span>
      <h3 className="mt-4 font-display text-xl sm:text-2xl font-black tracking-tight text-slate-900 leading-tight">
        Should you start freelancing?{" "}
        <span className="text-blue-600">Find out in 3 questions.</span>
      </h3>
      <p className="mt-3 text-slate-600">
        Three taps. We&apos;ll diagnose your work-life risk and Avni will tell you exactly which freelance services your skills can sell — in ₹.
      </p>
      <button
        onClick={onStart}
        data-testid="reality-quiz-start"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 text-white px-5 py-3.5 text-sm font-medium hover:bg-blue-700 hover:-translate-y-0.5 transition shadow-[0_18px_38px_-18px_rgba(37,99,235,0.7)]"
      >
        Start the reality check <ArrowRight size={16} />
      </button>
      <ul className="mt-5 space-y-2 text-sm text-slate-600">
        <li className="flex items-start gap-2"><Check size={14} className="text-emerald-500 mt-0.5" /> Anonymous — no email required</li>
        <li className="flex items-start gap-2"><Check size={14} className="text-emerald-500 mt-0.5" /> Personalized ₹ income ideas at the end</li>
        <li className="flex items-start gap-2"><Check size={14} className="text-emerald-500 mt-0.5" /> ~30 seconds</li>
      </ul>
    </div>
  );
}

function QuizStep({ q, idx, total, onAnswer }) {
  const pct = ((idx + 1) / total) * 100;
  return (
    <div data-testid={`reality-quiz-step-${idx}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-blue-600">Step {idx + 1} of {total}</span>
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">{q.keyword}</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-blue-500 to-sky-400 transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-5 flex items-start gap-3">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100 shrink-0">
          <q.icon size={20} />
        </span>
        <h3 className="font-display text-lg sm:text-xl md:text-2xl font-black text-slate-900 leading-tight">{q.question}</h3>
      </div>

      <div className="mt-5 space-y-2">
        {q.options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onAnswer(opt.value)}
            data-testid={`reality-quiz-answer-${q.id}-${opt.value}`}
            className="w-full text-left flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-blue-400 hover:bg-blue-50/40 transition group"
          >
            <span className="text-sm font-medium text-slate-800 group-hover:text-blue-700">{opt.label}</span>
            <ArrowRight size={14} className="text-slate-300 group-hover:text-blue-600 transition" />
          </button>
        ))}
      </div>
    </div>
  );
}

function QuizDone({ answers, onAskAvni, onRestart }) {
  const score = Object.values(answers).filter((v) => v === "yes" || v === "always").length;

  // Diagnosis verdict by risk score (2+ red flags = red zone, 1 = yellow, 0 = green).
  let verdict;
  if (score >= 2) {
    verdict = { label: "Red zone", tone: "text-red-700 bg-red-50 border-red-200", headline: "Your single salary is a high-risk plan." };
  } else if (score === 1) {
    verdict = { label: "Yellow zone", tone: "text-amber-700 bg-amber-50 border-amber-200", headline: "You should build a backup income stream now." };
  } else {
    verdict = { label: "Green-but-curious", tone: "text-emerald-700 bg-emerald-50 border-emerald-200", headline: "Smart move — diversify before you need to." };
  }

  return (
    <div data-testid="reality-quiz-done">
      <span className={`inline-flex items-center gap-1.5 rounded-full text-xs font-medium px-3 py-1 border ${verdict.tone}`}>
        Diagnosis · {verdict.label}
      </span>
      <h3 className="mt-4 font-display text-xl sm:text-2xl font-black tracking-tight text-slate-900 leading-tight">{verdict.headline}</h3>
      <p className="mt-3 text-slate-600">
        Avni has 3 specific freelance services ready for you — with realistic ₹ pricing and a 7-day plan to land your first gig.
      </p>

      <button
        onClick={onAskAvni}
        data-testid="reality-quiz-ask-avni"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 text-white px-5 py-3.5 text-sm font-medium hover:bg-blue-700 hover:-translate-y-0.5 transition shadow-[0_18px_38px_-18px_rgba(37,99,235,0.7)]"
      >
        Get my personalized freelance plan from Avni <ArrowRight size={16} />
      </button>

      <button
        onClick={onRestart}
        data-testid="reality-quiz-restart"
        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white text-slate-600 px-4 py-2.5 text-xs font-medium hover:border-blue-300 hover:text-blue-700"
      >
        <RotateCcw size={12} /> Restart quiz
      </button>
    </div>
  );
}
